import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, KeyRound, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) return setError('Le mot de passe doit contenir au moins 6 caractères.');
    if (password !== confirm) return setError('Les deux mots de passe ne sont pas identiques.');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/auth'), 2500);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Une erreur est survenue.');
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pr-12 text-white placeholder-white/30 focus:outline-none focus:border-[#E9641A] transition-colors";

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
      <div className="relative z-10 w-full max-w-md px-6 animate-scaleIn">
        <div className="flex justify-center mb-10">
          <img src={LOGO_URL} alt="Maya+" className="h-14" />
        </div>
        <div className="bg-[#1a0e2e]/80 backdrop-blur-xl rounded-xl p-8 border border-white/5" data-testid="reset-password-card">
          {done ? (
            <div className="text-center py-6" data-testid="reset-success">
              <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-4" />
              <h1 className="text-xl font-bold mb-2 text-white">Mot de passe modifié !</h1>
              <p className="text-[#A1A1AA] text-sm">Redirection vers la page de connexion…</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-center mb-2 text-white" data-testid="reset-title">Nouveau mot de passe</h1>
              <p className="text-[#A1A1AA] text-sm text-center mb-8">Choisissez un nouveau mot de passe pour votre compte Maya+.</p>
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg mb-4" data-testid="reset-error">{error}</div>
              )}
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-1.5">Nouveau mot de passe</label>
                  <div className="relative">
                    <input data-testid="reset-password-input" type={showPass ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" required minLength={6} />
                    <button type="button" onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                      {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-1.5">Confirmer le mot de passe</label>
                  <input data-testid="reset-confirm-input" type={showPass ? 'text' : 'password'} value={confirm}
                    onChange={e => setConfirm(e.target.value)} className={inputCls} placeholder="••••••••" required minLength={6} />
                </div>
                <button data-testid="reset-submit-btn" type="submit" disabled={loading}
                  className="w-full bg-[#E9641A] hover:bg-[#FE975E] text-white font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <><KeyRound size={17} /> Valider le nouveau mot de passe</>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
